<template>
  <div class="news-container">
    <div class="news-header">
      <h1>新闻资讯</h1>
      <p>了解公司最新动态和行业信息</p>
    </div>
    
    <div class="news-content">
      <!-- 新闻类型分类组件 -->
      <div class="news-categories">
        <div 
          v-for="category in categories"
          :key="category.id"
          :class="['category-item', { active: activeCategoryId === category.id }]"
          @click="switchCategory(category)"
        >
          {{ category.name }}
        </div>
      </div>
      
      <!-- 新闻列表组件 -->
      <div class="news-list">
        <div v-for="news in newsList" :key="news.id" class="news-item" @click="goToDetail(news)">
          <div class="news-image" v-if="news.headImgUrl">
            <img :src="news.headImgUrl" :alt="news.title" referrerpolicy="no-referrer">
          </div>
          <div class="news-info">
            <div class="news-type">{{ news.catName || currentCategoryName }}</div>
            <h3 class="news-title">{{ news.title }}</h3>
            <p class="news-date">{{ formatNewsDate(news) }}</p>
            <p class="news-content">{{ news.secTitle || getPlainText(news.content) }}</p>
          </div>
        </div>
      </div>
      
      <!-- 分页功能（预留） -->
      <div class="pagination">
        <button class="page-btn" disabled>上一页</button>
        <span class="page-info">第 1 页，共 1 页</span>
        <button class="page-btn" disabled>下一页</button>
      </div>
    </div>
  </div>
</template>

<script>
import { articleApi, articleCatApi } from '../api'

export default {
  name: 'News',
  data() {
    return {
      categories: [],
      activeCategoryId: null,
      newsList: [],
      loading: false,
      pendingCatId: null
    }
  },
  computed: {
    currentCategoryName() {
      const current = this.categories.find(item => item.id === this.activeCategoryId)
      return current ? current.name : ''
    }
  },
  mounted() {
    // 从 sessionStorage 读取待处理的 catId
    const savedCatId = sessionStorage.getItem('currentCatId')
    if (savedCatId) {
      this.pendingCatId = savedCatId
      // 读取后清除，避免重复使用
      sessionStorage.removeItem('currentCatId')
    }
    this.fetchNewsCategories()
  },
  methods: {
    selectCategoryById(catId) {
      if (!catId || !this.categories.length) {
        return
      }
      const targetCategory = this.categories.find(cat => String(cat.id) === String(catId))
      if (targetCategory && targetCategory.id !== this.activeCategoryId) {
        this.activeCategoryId = targetCategory.id
        this.fetchNewsListByCatId(targetCategory.id)
      }
    },
    // 去除HTML标签，只保留纯文本
    getPlainText(html) {
      if (!html) return ''
      return html
        .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
        .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
        .replace(/<[^>]+>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&amp;/g, '&')
        .replace(/&quot;/g, '"')
        .replace(/\s+/g, ' ')
        .trim()
    },
    async fetchNewsCategories() {
      try {
        const response = await articleCatApi.dataByPcode('news')
        const categoryList = this.parseCategoryList(response)
        // 在最前面添加“新闻资讯”tab，按 code=news 查询全部文章
        this.categories = [
          { id: 'code_news', name: '新闻资讯', isCodeTab: true },
          ...categoryList
        ]
        if (this.categories.length > 0) {
          // 如果有待处理的catId，优先选中对应的分类
          if (this.pendingCatId) {
            const targetCategory = categoryList.find(cat => String(cat.id) === String(this.pendingCatId))
            if (targetCategory) {
              this.activeCategoryId = targetCategory.id
              this.pendingCatId = null
              await this.fetchNewsListByCatId(this.activeCategoryId)
              return
            }
          }
          // 默认选中“新闻资讯”tab
          this.activeCategoryId = this.categories[0].id
          await this.fetchNewsListByCatId(this.activeCategoryId)
        }
      } catch (error) {
        console.error('获取新闻分类失败:', error)
      }
    },
    async switchCategory(category) {
      if (!category || category.id === this.activeCategoryId) {
        return
      }
      this.activeCategoryId = category.id
      await this.fetchNewsListByCatId(category.id)
    },
    async fetchNewsListByCatId(catId) {
      if (!catId) {
        this.newsList = []
        return
      }
      this.loading = true
      try {
        // “新闻资讯”tab 按 code=news 查询全部文章
        const params = catId === 'code_news' ? { code: 'news' } : { catId }
        const response = await articleApi.data(params)
        this.newsList = this.parseNewsList(response)
      } catch (error) {
        console.error('获取新闻列表失败:', error)
        this.newsList = []
      } finally {
        this.loading = false
      }
    },
    parseCategoryList(response) {
      if (response && (response.code === 0 || response.code === 200) && Array.isArray(response.data)) {
        return response.data
      }
      if (Array.isArray(response)) {
        return response
      }
      return []
    },
    parseNewsList(response) {
      if (response && (response.code === 0 || response.code === 200) && Array.isArray(response.data)) {
        return response.data
      }
      if (Array.isArray(response)) {
        return response
      }
      return []
    },
    formatNewsDate(news) {
      const rawDate = news.titleDate || news.crTime || news.upTime
      if (!rawDate) {
        return ''
      }
      const date = new Date(rawDate)
      if (Number.isNaN(date.getTime())) {
        return String(rawDate).slice(0, 10)
      }
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    goToDetail(news) {
      if (!news || !news.id) {
        return
      }
      // 触发页面切换事件，跳转到文章详情页
      window.dispatchEvent(new CustomEvent('switchPage', {
        detail: { page: 'article', articleId: news.id }
      }))
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }
}
</script>

<style scoped>
.news-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
}

.news-header {
  text-align: center;
  margin-bottom: 40px;
}

.news-header h1 {
  font-size: 36px;
  color: #333;
  margin-bottom: 10px;
}

.news-header p {
  font-size: 16px;
  color: #666;
}

.news-content {
  display: flex;
  flex-direction: column;
  gap: 30px;
}

.news-categories {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 20px;
  border-bottom: 1px solid #eaeaea;
  padding-bottom: 15px;
}

.category-item {
  padding: 8px 20px;
  background-color: #f5f5f5;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 14px;
}

.category-item:hover {
  background-color: #e0e0e0;
}

.category-item.active {
  background-color: #1890ff;
  color: white;
}

.news-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.news-item {
  display: flex;
  gap: 20px;
  padding: 20px;
  background-color: #f9f9f9;
  border-radius: 8px;
  transition: all 0.3s ease;
  cursor: pointer;
}

.news-item:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.news-image {
  flex: 0 0 200px;
  height: 140px;
  overflow: hidden;
  border-radius: 4px;
}

.news-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.news-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.news-type {
  display: inline-block;
  padding: 2px 10px;
  background-color: #e6f7ff;
  color: #1890ff;
  font-size: 12px;
  border-radius: 10px;
  align-self: flex-start;
}

.news-title {
  font-size: 18px;
  color: #333;
  margin: 0;
  font-weight: 600;
}

.news-date {
  font-size: 14px;
  color: #999;
  margin: 0;
}

.news-content {
  font-size: 14px;
  color: #666;
  line-height: 1.5;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 15px;
  margin-top: 30px;
}

.page-btn {
  padding: 8px 16px;
  border: 1px solid #d9d9d9;
  background-color: white;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.page-btn:hover:not(:disabled) {
  border-color: #1890ff;
  color: #1890ff;
}

.page-btn:disabled {
  color: #bfbfbf;
  cursor: not-allowed;
}

.page-info {
  font-size: 14px;
  color: #666;
}

/* 响应式布局 */
@media (max-width: 768px) {
  .news-item {
    flex-direction: column;
  }
  
  .news-image {
    flex: none;
    width: 100%;
    height: 200px;
  }
  
  .news-categories {
    justify-content: center;
  }
  
  .news-header h1 {
    font-size: 28px;
  }
}

@media (max-width: 480px) {
  .news-container {
    padding: 20px 15px;
  }
  
  .news-header h1 {
    font-size: 24px;
  }
  
  .news-image {
    height: 150px;
  }
  
  .category-item {
    padding: 6px 16px;
    font-size: 12px;
  }
}
</style>
